import { useState, useRef, useCallback } from "react";
import { beforeAfterCases, BeforeAfterItem } from "@/mocks/beforeAfter";

const typeColors: Record<string, string> = {
  "아파트": "bg-emerald-100 text-emerald-700",
  "오피스텔": "bg-sky-100 text-sky-700",
  "단독주택": "bg-amber-100 text-amber-700",
};

/* ── 이미지 편집 모달 ── */
interface EditModalProps {
  item: BeforeAfterItem;
  onSave: (id: string, before: string, after: string) => void;
  onClose: () => void;
}

const EditModal = ({ item, onSave, onClose }: EditModalProps) => {
  const [beforeUrl, setBeforeUrl] = useState(item.before);
  const [afterUrl, setAfterUrl] = useState(item.after);
  const [beforeErr, setBeforeErr] = useState(false);
  const [afterErr, setAfterErr] = useState(false);

  const handleSave = () => {
    onSave(item.id, beforeUrl.trim() || item.before, afterUrl.trim() || item.after);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-stone-100">
          <div>
            <h3 className="text-sm font-black text-stone-900">이미지 변경</h3>
            <p className="text-xs text-stone-400 mt-0.5">{item.title}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-stone-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Before */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-stone-700 mb-2">
              <span className="bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">시공 전</span>
              이미지 URL
            </label>
            <input
              value={beforeUrl}
              onChange={(e) => { setBeforeUrl(e.target.value); setBeforeErr(false); }}
              placeholder="https://... 이미지 주소를 붙여넣으세요"
              className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400 transition-colors"
            />
            {beforeUrl && (
              <div className="mt-2 rounded-lg overflow-hidden border border-stone-100" style={{ height: "100px" }}>
                <img
                  src={beforeUrl}
                  alt="시공 전 미리보기"
                  className="w-full h-full object-cover object-top"
                  onError={() => setBeforeErr(true)}
                />
                {beforeErr && (
                  <div className="w-full h-full bg-stone-100 flex items-center justify-center -mt-[100px]">
                    <p className="text-xs text-stone-400">이미지를 불러올 수 없어요</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* After */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-stone-700 mb-2">
              <span className="bg-stone-900 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">시공 후</span>
              이미지 URL
            </label>
            <input
              value={afterUrl}
              onChange={(e) => { setAfterUrl(e.target.value); setAfterErr(false); }}
              placeholder="https://... 이미지 주소를 붙여넣으세요"
              className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400 transition-colors"
            />
            {afterUrl && (
              <div className="mt-2 rounded-lg overflow-hidden border border-stone-100" style={{ height: "100px" }}>
                <img
                  src={afterUrl}
                  alt="시공 후 미리보기"
                  className="w-full h-full object-cover object-top"
                  onError={() => setAfterErr(true)}
                />
                {afterErr && (
                  <div className="w-full h-full bg-stone-100 flex items-center justify-center -mt-[100px]">
                    <p className="text-xs text-stone-400">이미지를 불러올 수 없어요</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <p className="text-[11px] text-stone-400 leading-relaxed">
            <i className="ri-information-line mr-1" />
            이미지 URL은 직접 촬영한 사진을 이미지 호스팅 서비스(예: imgur, Google Drive 공유링크 등)에 올린 후 주소를 붙여넣으세요.
          </p>
        </div>

        <div className="px-6 pb-5 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-stone-200 text-stone-600 text-sm font-semibold rounded-xl hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-2.5 bg-stone-900 text-white text-sm font-bold rounded-xl hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            저장하기
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── 슬라이더 카드 ── */
interface SliderCardProps {
  item: BeforeAfterItem;
  onEdit: () => void;
}

const SliderCard = ({ item, onEdit }: SliderCardProps) => {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updatePos = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPos((x / rect.width) * 100);
  }, []);

  const onMouseDown = (e: React.MouseEvent) => { e.preventDefault(); setIsDragging(true); };
  const onMouseMove = useCallback((e: React.MouseEvent) => { if (isDragging) updatePos(e.clientX); }, [isDragging, updatePos]);
  const onMouseUp = useCallback(() => setIsDragging(false), []);
  const onTouchMove = useCallback((e: React.TouchEvent) => { updatePos(e.touches[0].clientX); }, [updatePos]);

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-stone-100">
      {/* Slider Image Area */}
      <div
        ref={containerRef}
        className="relative overflow-hidden select-none"
        style={{ height: "260px", cursor: isDragging ? "grabbing" : "grab" }}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchMove={onTouchMove}
        onTouchEnd={onMouseUp}
      >
        {/* After (base) */}
        <img src={item.after} alt="시공 후" className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none" draggable={false} />

        {/* Before (clipped) */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${sliderPos}%` }}>
          <img
            src={item.before}
            alt="시공 전"
            className="absolute inset-0 h-full object-cover object-top pointer-events-none"
            style={{ width: containerRef.current ? `${containerRef.current.offsetWidth}px` : "100%" }}
            draggable={false}
          />
        </div>

        {/* Divider */}
        <div className="absolute top-0 bottom-0 w-0.5 bg-white" style={{ left: `${sliderPos}%`, transform: "translateX(-50%)" }}>
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 bg-white rounded-full flex items-center justify-center"
            style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.18)" }}
            onMouseDown={onMouseDown}
            onTouchStart={() => setIsDragging(true)}
          >
            <div className="flex items-center gap-0.5">
              <i className="ri-arrow-left-s-line text-stone-600 text-sm" />
              <i className="ri-arrow-right-s-line text-stone-600 text-sm" />
            </div>
          </div>
        </div>

        {/* Labels */}
        <div className="absolute top-3 left-3 pointer-events-none">
          <span className="bg-black/60 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">시공 전</span>
        </div>
        <div className="absolute top-3 right-3 pointer-events-none">
          <span className="bg-stone-900 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">시공 후</span>
        </div>

        {/* Edit Button */}
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-white/90 hover:bg-white text-stone-700 text-[11px] font-bold px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-image-edit-line text-xs" />
          이미지 변경
        </button>

        {sliderPos === 50 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 pointer-events-none">
            <span className="bg-white/80 text-stone-600 text-[10px] font-semibold px-3 py-1 rounded-full flex items-center gap-1">
              <i className="ri-drag-move-line text-xs" />
              드래그해서 비교
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${typeColors[item.type] || "bg-stone-100 text-stone-600"}`}>
            {item.type}
          </span>
          <span className="text-xs text-stone-400"><i className="ri-map-pin-line mr-0.5" />{item.location}</span>
        </div>
        <h3 className="text-sm font-black text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
        <p className="text-xs text-stone-500 mb-2"><i className="ri-lock-line mr-1" />{item.product}</p>
        <p className="text-xs text-stone-600 leading-relaxed">{item.desc}</p>
      </div>
    </div>
  );
};

/* ── 메인 섹션 ── */
const BeforeAfterSection = () => {
  const [cases, setCases] = useState<BeforeAfterItem[]>(beforeAfterCases);
  const [editingId, setEditingId] = useState<string | null>(null);

  const editingItem = cases.find((c) => c.id === editingId) ?? null;

  const handleSave = (id: string, before: string, after: string) => {
    setCases((prev) => prev.map((c) => c.id === id ? { ...c, before, after } : c));
  };

  return (
    <section className="py-16 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-2">Before &amp; After</p>
            <h2 className="text-2xl md:text-3xl font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              시공 전후 비교
            </h2>
            <p className="text-sm text-stone-500 mt-2">슬라이더를 드래그해서 시공 전후를 직접 비교해보세요.</p>
          </div>
          <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 py-2 self-start sm:self-auto">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-drag-move-line text-stone-500 text-sm" />
            </div>
            <span className="text-xs font-semibold text-stone-600">드래그로 비교</span>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cases.map((item) => (
            <SliderCard key={item.id} item={item} onEdit={() => setEditingId(item.id)} />
          ))}
        </div>

        {/* Bottom Note */}
        <div className="mt-8 flex items-start gap-3 bg-white rounded-xl p-4 border border-stone-100">
          <div className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-lg flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-stone-600 text-sm" />
          </div>
          <div>
            <p className="text-xs font-bold text-stone-700 mb-0.5">전문 기사 직접 시공</p>
            <p className="text-xs text-stone-500 leading-relaxed">
              도어준의 모든 설치는 자격증 보유 전문 기사가 직접 방문하여 시공합니다. 기존 도어락 철거부터 새 제품 설치, 작동 테스트, 사용법 안내까지 한 번에 완료됩니다.
            </p>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingItem && (
        <EditModal
          item={editingItem}
          onSave={handleSave}
          onClose={() => setEditingId(null)}
        />
      )}
    </section>
  );
};

export default BeforeAfterSection;
